import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, IconButton, LinearProgress, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import { Pill, GapNote, helperProps, compactPlaceholderSx } from './common'
import { downloadSheets, marginWorkingRows, allocationBdRows, allocationInternalRows, serviceabilityRows, exceptionRows } from './fileExports'
import { CARD_TYPE_API_TO_LABEL, serviceLabel } from '../apiMap'

// Fallback section names → API serviceType, used only when the version's real
// services aren't passed in.
const NAME_TO_SERVICE = {
  'FWD Surface': 'FWD_SURFACE', Surface: 'FWD_SURFACE',
  'FWD Air': 'FWD_AIR', Air: 'FWD_AIR',
  'SDD/NDD': 'SDD_NDD', 'SDD & NDD': 'SDD_NDD', 'SDD / NDD': 'SDD_NDD',
  'Reverse (RVP)': 'RVP', RVP: 'RVP',
}
const ZONE_ORDER = ['Local', 'Within State', 'Metros', 'ROI', 'Special']
const slabKg = (s) => parseFloat(String(s)) || 0
const PACK_FILES = ['Client Proposal', 'Internal Margin Working', 'Approximate allocation', 'Allocation — internal view', 'Serviceability and TAT', 'Exception & risk report']
// Expected format per slot (mockup: docx · xlsx · xlsx · xlsx · xlsx · docx/pdf).
const PACK_TYPES = ['docx', 'xlsx', 'xlsx', 'xlsx', 'xlsx', 'docx / pdf']

/**
 * CounterEditorDialog — the sectioned in-UI counter builder.
 * Each service section shows an editable slab × zone rate grid, seeded from the
 * deal's real computed lanes; on submit it builds the real `rateCards` payload
 * (POST /counter). Submit unlocks once every section is marked reviewed.
 * role: 'l1' | 'fh' — controls the destination options.
 */
export default function CounterEditorDialog({ open, onClose, role = 'l1', mode = 'ui', sections, lanes = [], rtoLanes = [], route = null, cardType = null, onSubmit, submitting = false }) {
  const { enqueueSnackbar } = useSnackbar()

  // Rate-card format is a Step-1 decision on the deal, not something a reviewer
  // picks while countering — reflect the deal's real cardType, don't offer a
  // choice that doesn't exist.
  const apiMode = cardType === 'COURIER_WISE' ? 'COURIER_WISE' : 'BLENDED'

  // Per (courier × service) in COURIER_WISE mode, or per service in BLENDED — { zones[],
  // slabs[], seed[slab][zone] } from the real computed lanes. courierId/courierParent have
  // been on every lane object all along (D2C Courier Selection Pivot §4); blended mode
  // deliberately still collapses across couriers into one rate, matching the deal's format.
  const laneStruct = useMemo(() => {
    const acc = {}
    ;(lanes || []).forEach((l) => {
      const key = apiMode === 'COURIER_WISE' ? `${l.courierId || 'unknown'}::${l.serviceType}` : l.serviceType
      if (!acc[key]) acc[key] = { serviceType: l.serviceType, courierId: l.courierId || null, courierParent: l.courierParent || null, zones: new Set(), slabs: new Set(), seed: {} }
      if (l.zone) acc[key].zones.add(l.zone)
      if (l.weightSlab) acc[key].slabs.add(l.weightSlab)
      acc[key].seed[l.weightSlab] = acc[key].seed[l.weightSlab] || {}
      if (acc[key].seed[l.weightSlab][l.zone] == null && l.revenueRate != null) acc[key].seed[l.weightSlab][l.zone] = l.revenueRate
    })
    const out = {}
    Object.entries(acc).forEach(([key, v]) => {
      const zones = [...v.zones].sort((a, b) => (ZONE_ORDER.indexOf(a) - ZONE_ORDER.indexOf(b)))
      const slabs = [...v.slabs].sort((a, b) => slabKg(a) - slabKg(b))
      out[key] = { serviceType: v.serviceType, courierId: v.courierId, courierParent: v.courierParent, zones, slabs, seed: v.seed }
    })
    return out
  }, [lanes, apiMode])

  // COURIER_WISE: one section per (courier × service), derived straight from laneStruct —
  // that's the only place the real per-courier breakdown lives, so the `sections`/fallback
  // list (service-only) can't be used here. BLENDED keeps the exact previous behavior.
  const SECTIONS = useMemo(() => {
    if (apiMode === 'COURIER_WISE') {
      return Object.entries(laneStruct).map(([key, v], i) => ({
        id: i, key,
        name: `${v.courierParent || v.courierId || 'Courier'} · ${serviceLabel(v.serviceType)}`,
        serviceType: v.serviceType,
      }))
    }
    if (Array.isArray(sections) && sections.length) {
      return sections.map((s, i) => ({ id: i, key: s.serviceType, name: s.name, serviceType: s.serviceType }))
    }
    return ['FWD Surface', 'FWD Air', 'Reverse (RVP)'].map((s, i) => ({ id: i, key: NAME_TO_SERVICE[s], name: s, serviceType: NAME_TO_SERVICE[s] }))
  }, [sections, apiMode, laneStruct])

  const [reviewed, setReviewed] = useState([])
  // Default to "Send counter to Sales Leader" — for EVERY route, per the Aug-2026
  // loss-deal flow (QA #6/#7): sub-10% deals move freely through L1 (counter → SL,
  // return, escalate); only APPROVAL is FH-reserved. destination:"SL" must be sent
  // explicitly — anything else routes a route-3 counter straight to FH (T12 default).
  const [dest, setDest] = useState('sl')
  const [rates, setRates] = useState({}) // section key → slab → zone → string

  // Seed editable cells from the lanes when the dialog opens.
  useEffect(() => {
    if (!open) return
    const seed = {}
    Object.entries(laneStruct).forEach(([st, v]) => {
      seed[st] = {}
      v.slabs.forEach((slab) => {
        seed[st][slab] = {}
        v.zones.forEach((z) => { const val = v.seed[slab]?.[z]; seed[st][slab][z] = val != null ? String(val) : '' })
      })
    })
    setRates(seed)
    setReviewed([])
    setPackFiles({})
  }, [open, laneStruct])

  // Upload-counter mode: the six-file pack, each slot must have a file attached.
  const [packFiles, setPackFiles] = useState({}) // index → File
  const [packNote, setPackNote] = useState('') // optional version label / note
  const attachPackFile = (i, file) => { if (file) setPackFiles((p) => ({ ...p, [i]: file })) }
  const packDone = Object.keys(packFiles).length
  const allAttached = packDone >= PACK_FILES.length
  // "⬇ Download counter pack template — pre-filled with the current version" (mockup):
  // one workbook, six sheets, built from the deal's real computed lanes.
  const downloadPackTemplate = () => {
    const rateRows = []
    Object.values(laneStruct).forEach((v) => v.slabs.forEach((slab) => {
      rateRows.push({
        ...(apiMode === 'COURIER_WISE' ? { Courier: v.courierParent || v.courierId || '' } : {}),
        Service: serviceLabel(v.serviceType), 'Weight slab': slab,
        ...v.zones.reduce((a, z) => ({ ...a, [z]: v.seed[slab]?.[z] ?? '' }), {}),
      })
    }))
    downloadSheets('D2C_Counter_Pack_Template.xlsx', [
      { name: 'Rate Card (counter)', rows: rateRows },
      { name: 'Margin Working', rows: marginWorkingRows(lanes) },
      // D2C RTO Margin Breakout §5 — the RTO breakout as its own tab, same file.
      ...(rtoLanes.length ? [{ name: 'Margin Working (RTO)', rows: marginWorkingRows(rtoLanes) }] : []),
      { name: 'Approximate Allocation', rows: allocationBdRows(lanes) },
      { name: 'Allocation Internal', rows: allocationInternalRows(lanes) },
      { name: 'Serviceability', rows: serviceabilityRows(lanes) },
      { name: 'Exceptions', rows: exceptionRows(lanes) },
    ])
  }

  const total = SECTIONS.length
  const done = mode === 'upload' ? packDone : reviewed.length
  const pct = mode === 'upload'
    ? Math.round((packDone / PACK_FILES.length) * 100)
    : (total ? Math.round((done / total) * 100) : 0)
  const allReviewed = reviewed.length >= total
  const canSubmit = mode === 'upload' ? allAttached : allReviewed
  const markReviewed = (id) => setReviewed((prev) => (prev.includes(id) ? prev : [...prev, id]))
  const setCell = (st, slab, zone, val) => setRates((p) => ({ ...p, [st]: { ...p[st], [slab]: { ...(p[st]?.[slab] || {}), [zone]: val } } }))

  const submit = () => {
    if (!canSubmit || submitting) return
    if (onSubmit) {
      const destination = dest === 'final' ? 'FINAL' : dest === 'fh' ? 'FH' : 'SL'
      // Sections without computed lanes are OMITTED (never send an empty rateCards array —
      // the engine would price that service at zero revenue, crash the margin to Route 3 and
      // the state machine would reject the transition). In upload mode `rates` still holds
      // the seeded carry-forward of the current client rates, so numbers stay unchanged.
      // COURIER_WISE: several sections can share one serviceType (one per courier) — group
      // them back into a single API section per service, with one rateCards entry per
      // courier carrying its real courierId. BLENDED stays exactly one entry, courierId: null.
      const bySvc = {}
      SECTIONS.forEach((s) => {
        const struct = laneStruct[s.key]
        if (!struct?.slabs?.length) return
        const rows = struct.slabs.map((slab) => ({
          weightSlab: slab,
          zoneRates: struct.zones.reduce((acc, z) => {
            const v = rates[s.key]?.[slab]?.[z]
            if (v !== '' && v != null) acc[z] = Number(v)
            return acc
          }, {}),
        }))
        if (!bySvc[s.serviceType]) bySvc[s.serviceType] = []
        bySvc[s.serviceType].push({ courierId: apiMode === 'COURIER_WISE' ? struct.courierId : null, rows })
      })
      const apiSections = Object.entries(bySvc).map(([serviceType, rateCards]) => ({ serviceType, reviewed: true, rateCards }))
      onSubmit({ mode: apiMode, destination, brandRfiRequired: false, sections: apiSections, note: mode === 'upload' ? packNote.trim() : '' })
      return
    }
    enqueueSnackbar(`Counter (${apiMode}) submitted.`, { variant: 'success' })
    setReviewed([])
    onClose && onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={compactPlaceholderSx}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 15, fontWeight: 700, color: 'primary.dark' }}>
        {mode === 'ui' ? 'Create Counter Proposal in UI' : 'Upload Counter — full six-file pack'}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {mode === 'ui' ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Rate-card format:</Typography>
              <Pill label={CARD_TYPE_API_TO_LABEL[apiMode] || apiMode} styleKey="purple" />
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>— fixed for this deal, set when it was created</Typography>
            </Stack>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{done} of {total} sections reviewed</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{pct}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
            </Box>
            {apiMode === 'COURIER_WISE' && SECTIONS.length === 0 && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>No computed lanes for this deal yet — nothing to counter.</Typography>
            )}
            {SECTIONS.map((s) => {
              const isDone = reviewed.includes(s.id)
              const struct = laneStruct[s.key]
              return (
                <Box key={s.id} sx={{ border: '1px solid', borderColor: isDone ? 'success.light' : 'divider', borderRadius: '9px', p: 1.5, mb: 1, bgcolor: isDone ? '#F6FEF9' : '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12, flex: 1 }}>{s.id + 1}. {s.name}</Typography>
                    {isDone ? <Pill label="Reviewed" styleKey="approved" /> : (
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none', fontSize: 12 }} onClick={() => markReviewed(s.id)}>Mark reviewed → next</Button>
                    )}
                  </Box>
                  {struct && struct.slabs.length ? (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={thS}>Weight slab</th>
                            {struct.zones.map((z) => <th key={z} style={thS}>{z} (₹)</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {struct.slabs.map((slab) => (
                            <tr key={slab}>
                              <td style={tdS}><b>{slab}</b></td>
                              {struct.zones.map((z) => (
                                <td key={z} style={tdS}>
                                  <TextField size="small" value={rates[s.key]?.[slab]?.[z] ?? ''} onChange={(e) => setCell(s.key, slab, z, e.target.value)} sx={{ width: 80 }} inputProps={{ style: { textAlign: 'right', padding: '4px 6px' } }} />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No computed lanes for this service — nothing to counter.</Typography>
                  )}
                </Box>
              )
            })}
          </>
        ) : (
          <Box>
            <GapNote id="counter_upload" />
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
              A counter changes the commercials, so the whole pack must be re-uploaded — the counter proposal itself plus the five working files it regenerates. Submit unlocks once all six are attached.
            </Typography>
            <Button size="small" variant="outlined" sx={{ textTransform: 'none', fontSize: 12, mb: 1.25 }} onClick={downloadPackTemplate}>
              ⬇ Download counter pack template — pre-filled with the current version
            </Button>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{packDone} of {PACK_FILES.length} files attached</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{pct}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
            </Box>
            {PACK_FILES.map((f, i) => {
              const file = packFiles[i]
              return (
                <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: file ? 'success.light' : 'divider', bgcolor: file ? '#F6FEF9' : '#fff', borderRadius: '8px', p: 1, mb: 0.75 }}>
                  <Typography sx={{ fontSize: 12, flex: 1 }}>{i + 1}. {f} <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>· {PACK_TYPES[i]}</Typography></Typography>
                  {file && <Typography sx={{ fontSize: 12, color: 'success.main', fontWeight: 600, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✓ {file.name}</Typography>}
                  <Button size="small" variant="outlined" component="label" sx={{ textTransform: 'none', fontSize: 12 }}>
                    {file ? 'Replace' : 'Attach'}
                    <input type="file" hidden onChange={(e) => attachPackFile(i, e.target.files?.[0])} />
                  </Button>
                </Box>
              )
            })}
            <TextField fullWidth size="small" label="Version label / note (optional)" value={packNote} onChange={(e) => setPackNote(e.target.value)} sx={{ mt: 0.75 }} {...helperProps("Posted to the deal's comment thread with the counter.")} />
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Where should this counter go?</Typography>
        <RadioGroup value={dest} onChange={(e) => setDest(e.target.value)}>
          {role === 'l1' ? (
            /* Aug-2026 loss-deal flow (QA #6/#7): counter → SL is allowed on EVERY route,
               including sub-10% — the old "Route-3 counters go to FH" disable is gone. */
            <>
              <FormControlLabel value="sl" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>Send counter to Sales Leader</Typography>} />
              <FormControlLabel value="fh" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>Send to Functional Head (Pricing-authored change)</Typography>} />
            </>
          ) : (
            <>
              <FormControlLabel value="final" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>Apply &amp; release — final (FH sign-off)</Typography>} />
              <FormControlLabel value="sl" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>Send counter to Sales Leader</Typography>} />
            </>
          )}
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || submitting} onClick={submit} sx={{ textTransform: 'none' }}>
          {submitting ? 'Submitting…' : 'Submit counter'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const thS = { textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #e3e8f0', fontSize: 12, fontFamily: 'Poppins, sans-serif', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }
const tdS = { padding: '2px 6px', borderBottom: '1px solid #f1f5f9', fontFamily: 'Poppins, sans-serif' }
